import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { PricingRecommendationRow } from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import {
  RecommendationAction,
  RejectionReasonTag,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { resolveEffectiveAction } from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/effective-recommendation-action';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';
import { View } from 'react-native';

const REASON_TAG_LABELS: Record<RejectionReasonTag, string> = {
  [RejectionReasonTag.AtRisk]: i18n.t(
    'pricing.agent.review.reason.tag.at_risk',
  ),
  [RejectionReasonTag.SpecialDeal]: i18n.t(
    'pricing.agent.review.reason.tag.special_deal',
  ),
  [RejectionReasonTag.WrongData]: i18n.t(
    'pricing.agent.review.reason.tag.wrong_data',
  ),
};

interface Props {
  defaultAccepted: boolean;
  name: string;
  row: PricingRecommendationRow;
}

const STATUS_LABELS: Record<RecommendationAction, string> = {
  [RecommendationAction.Accepted]: i18n.t(
    'pricing.agent.review.status.accepted',
  ),
  [RecommendationAction.Overridden]: i18n.t(
    'pricing.agent.review.status.overridden',
  ),
  [RecommendationAction.Rejected]: i18n.t(
    'pricing.agent.review.status.rejected',
  ),
};

const buildReasonDetail = (
  tagIds: RejectionReasonTag[] | undefined,
  note: string | null | undefined,
): string | null => {
  const parts: string[] = [];
  if (tagIds?.length) {
    for (const id of tagIds) {
      parts.push(REASON_TAG_LABELS[id]);
    }
  }
  if (note?.trim()) {
    parts.push(note.trim());
  }
  return parts.length > 0 ? parts.join(', ') : null;
};

export const AgentNameCell = React.memo<Props>(
  ({ defaultAccepted, name, row }) => {
    const [styles, theme] = useStyles();

    const localAction = useRecommendationActionsStore(
      (state) => state.actions[row.uuid]?.action,
    );
    const reasonTags = useRecommendationActionsStore(
      (state) => state.actions[row.uuid]?.rejectionReason?.tags,
    );
    const reasonNote = useRecommendationActionsStore(
      (state) => state.actions[row.uuid]?.rejectionReason?.note,
    );

    const action = resolveEffectiveAction(
      localAction,
      row.reviewStatus,
      defaultAccepted,
    );

    const statusColors: Record<RecommendationAction, string> = {
      [RecommendationAction.Accepted]: theme.sk.color.text.success,
      [RecommendationAction.Overridden]: theme.sk.color.text.blue,
      [RecommendationAction.Rejected]: theme.sk.color.text.error,
    };
    const statusColor = action ? statusColors[action] : null;
    const reasonDetail =
      action === RecommendationAction.Rejected
        ? buildReasonDetail(reasonTags, reasonNote)
        : null;

    const label = action
      ? reasonDetail
        ? `${STATUS_LABELS[action]} - ${reasonDetail}`
        : STATUS_LABELS[action]
      : null;

    return (
      <View style={styles.wrapper}>
        <Typography numberOfLines={1} variant="paragraph-md-600">
          {name}
        </Typography>
        {label && statusColor ? (
          <Typography
            color={statusColor}
            numberOfLines={2}
            variant="paragraph-sm-600"
          >
            {label}
          </Typography>
        ) : null}
      </View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  wrapper: {
    flex: 1,
    gap: 2,
    paddingLeft: theme.sk.spacing.spaceLarge,
  },
}));
